/* eslint-disable arrow-body-style */
import { type ComponentPropsWithoutRef, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from './ErrorMessage';
import { textareaMaxLength } from '../../constants';
import {
  containsReservedCharacters,
  getCharacterCount,
  getInputRules,
  getWordCount,
  stripReservedCharacters,
  validateField,
} from '../../utils/form-utils';
import type { ErrorMessages, Rules } from '../../config/types';
import Details from './Details';

type Props = {
  name: string;
  numeric?: boolean;
  title: string;
  rules?: Rules;
  errorMessages?: ErrorMessages;
  detailsHeading?: string;
  detailsContent?: string;
  wordCount?: number;
  characterCount?: number;
  textRows?: number;
} & ComponentPropsWithoutRef<'textarea'>;

const Textarea = (props: Props) => {
  const {
    name,
    title,
    rules,
    disabled,
    defaultValue,
    numeric,
    errorMessages,
    detailsHeading,
    detailsContent,
    wordCount,
    characterCount,
    textRows,
    ...textareaProps
  } = props;

  const { register, getFieldState, getValues, setValue, watch } = useFormContext();

  const inputRules = useMemo(
    () => ({ ...getInputRules(rules ?? {}, title, textareaMaxLength) }),
    [rules, title],
  );

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue ?? '');
    }
  }, []);

  const hooksFormRegister = register(name, {
    validate: (value: string) => {
      const baseError = validateField(title, inputRules, value, errorMessages);
      if (baseError) return baseError;

      const stringValue = typeof value === 'string' ? value : String(value ?? '');
      if (typeof wordCount === 'number') {
        const wordsUsed = getWordCount(stringValue);
        if (wordsUsed > wordCount) {
          const over = wordsUsed - wordCount;
          return `You have ${over} word${over === 1 ? '' : 's'} too many`;
        }
      }

      if (typeof characterCount === 'number') {
        const charsUsed = getCharacterCount(stringValue);
        if (charsUsed > characterCount) {
          const over = charsUsed - characterCount;
          return `You have ${over} character${over === 1 ? '' : 's'} too many`;
        }
      }

      return undefined;
    },
  });
  const { error } = getFieldState(name);

  const rawValue = watch(name);
  const stringValue = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
  const wordsUsed = getWordCount(stringValue);
  const charsUsed = getCharacterCount(stringValue);
  const hasWordCount = typeof wordCount === 'number';
  const hasCharacterCount = typeof characterCount === 'number';
  const isWordOver = hasWordCount && wordsUsed > wordCount;
  const isCharacterOver = hasCharacterCount && charsUsed > characterCount;

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <textarea
        {...textareaProps}
        {...hooksFormRegister}
        disabled={disabled ?? false}
        onKeyDown={(e) => {
          // The e.key values are strings for control characters such as 'Backspace'
          // All non control characters will have a single character length
          if (
            e.key.length === 1 &&
            containsReservedCharacters(e.key, inputRules.reservedCharacters)
          )
            e.preventDefault();
        }}
        onPaste={(e) => {
          const clipboardData = e.clipboardData.getData('text');
          if (containsReservedCharacters(clipboardData, inputRules.reservedCharacters)) {
            e.preventDefault();
            const sanitized = stripReservedCharacters(
              clipboardData,
              inputRules.reservedCharacters,
            );
            const target = e.target as HTMLTextAreaElement;
            const start = target.selectionStart ?? target.value.length;
            const end = target.selectionEnd ?? target.value.length;
            const nextValue = `${target.value.slice(0, start)}${sanitized}${target.value.slice(
              end,
            )}`;
            setValue(name, nextValue, { shouldDirty: true, shouldValidate: true });
            requestAnimationFrame(() => {
              const cursor = start + sanitized.length;
              target.selectionStart = cursor;
              target.selectionEnd = cursor;
            });
          }
        }}
        className="govuk-textarea"
        rows={textRows ?? 5}
        inputMode={numeric ? 'numeric' : 'text'}
        data-testid={name}
      />
      {hasWordCount && (
        <p className={isWordOver ? 'govuk-error-message' : 'govuk-hint'} style={{ marginTop: 0 }}>
          {isWordOver
            ? `You have ${wordsUsed - wordCount} word${
                wordsUsed - wordCount === 1 ? '' : 's'
              } too many`
            : `You have ${wordCount - wordsUsed} word${
                wordCount - wordsUsed === 1 ? '' : 's'
              } remaining`}
        </p>
      )}
      {hasCharacterCount && (
        <p
          className={isCharacterOver ? 'govuk-error-message' : 'govuk-hint'}
          style={{ marginTop: 0 }}
        >
          {isCharacterOver
            ? `You have ${charsUsed - characterCount} character${
                charsUsed - characterCount === 1 ? '' : 's'
              } too many`
            : `You have ${characterCount - charsUsed} character${
                characterCount - charsUsed === 1 ? '' : 's'
              } remaining`}
        </p>
      )}
    </>
  );
};
Textarea.displayName = 'Textarea';

export default Textarea;
